import { useEffect, useState } from "react";
import axios from "axios";
import { Minus, Plus } from "lucide-react";
import { wInput, wLabel, StepTitle, Pill, NavRow } from "./shared";



const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROP_TYPES = ["House", "Apartment", "Unit", "Townhouse", "Office", "Warehouse", "Other"];
const FLOORS = ["Ground Floor", "1st Floor", "2nd Floor", "3rd Floor", "4th+ Floor"];
const YES_NO = ["Yes", "No"];
const ACCESS = ["Yes", "No", "Not Sure"];

const PillGroup = ({ label, options, value, onChange, testid }) => (
  <div className="mt-6">
    <label className={wLabel}>{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Pill key={o} label={o} active={value === o} onClick={() => onChange(o)} testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
      ))}
    </div>
  </div>
);

export const StepPropertyDetails = ({ title, value, onChange, onNext, onBack, testid }) => {
  const [tried, setTried] = useState(false);
  const set = (k, v) => onChange({ ...value, [k]: v });
  const go = () => (value.type ? onNext() : setTried(true));
  return (
    <div data-testid={testid}>
      <StepTitle title={title} sub="Helps our crew arrive prepared with the right equipment." />
      <PillGroup label="Property Type *" options={PROP_TYPES} value={value.type} onChange={(v) => set("type", v)} testid={`${testid}-type`} />
      {tried && !value.type && <p className="mt-2 text-sm font-semibold text-blaze" data-testid={`${testid}-error`}>Please select the property type.</p>}
      <PillGroup label="Which floor is the property on?" options={FLOORS} value={value.floor} onChange={(v) => set("floor", v)} testid={`${testid}-floor`} />
      <PillGroup label="Is there a lift/elevator?" options={YES_NO} value={value.lift} onChange={(v) => set("lift", v)} testid={`${testid}-lift`} />
      <PillGroup label="Are there stairs?" options={YES_NO} value={value.stairs} onChange={(v) => set("stairs", v)} testid={`${testid}-stairs`} />
      {value.stairs === "Yes" && (
        <div className="mt-4 flex items-center gap-4 rounded-2xl border border-line bg-white p-4" data-testid={`${testid}-flights`}>
          <span className="text-sm font-semibold text-slate-700">Number of flights of stairs</span>
          <div className="ml-auto flex items-center gap-3">
            <button type="button" onClick={() => set("stair_flights", Math.max(1, (value.stair_flights || 1) - 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-slate-600" data-testid={`${testid}-flights-minus`}><Minus className="h-4 w-4" /></button>
            <span className="w-6 text-center text-lg font-bold text-slate-900" data-testid={`${testid}-flights-count`}>{value.stair_flights || 1}</span>
            <button type="button" onClick={() => set("stair_flights", (value.stair_flights || 1) + 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-slate-600" data-testid={`${testid}-flights-plus`}><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <PillGroup label="Is there easy truck access?" options={ACCESS} value={value.truck_access} onChange={(v) => set("truck_access", v)} testid={`${testid}-access`} />
      <NavRow onBack={onBack} onNext={go} testid={`${testid}-next`} />
    </div>
  );
};

export const Step7DateTime = ({ data, patch, settings, onNext, onBack }) => {
  const [booked, setBooked] = useState([]);
  const [tried, setTried] = useState(false);
  const times = settings?.start_times || [];

  useEffect(() => {
    if (!data.moving_date) return;
    axios.get(`${API}/bookings/slots`, { params: { date: data.moving_date } })
      .then(({ data: r }) => setBooked(r.booked))
      .catch(() => setBooked([]));
  }, [data.moving_date]);

  const valid = data.moving_date && data.start_time;
  const go = () => (valid ? onNext() : setTried(true));
  return (
    <div data-testid="wz-step7">
      <StepTitle title="When are you moving?" sub="Booked-out times are unavailable." />
      <label className={wLabel}>Preferred Moving Date *</label>
      <input type="date" min={new Date().toLocaleDateString("en-CA")} className={`${wInput} max-w-xs text-slate-700`} value={data.moving_date} onChange={(e) => patch({ moving_date: e.target.value, start_time: "" })} data-testid="wz-date" />
      <div className="mt-6">
        <label className={wLabel}>Preferred Start Time *</label>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {times.map((s) => {
            const taken = booked.includes(s);
            const active = data.start_time === s;
            return (
              <button
                key={s}
                type="button"
                disabled={taken}
                onClick={() => patch({ start_time: s })}
                data-testid={`slot-${s.replace(/[:\s]/g, "-")}`}
                className={`rounded-xl border-2 px-2 py-3 text-sm font-semibold transition-all duration-200 active:scale-95 ${
                  taken ? "cursor-not-allowed border-line/50 text-slate-400 line-through" : active ? "border-blaze bg-blaze text-white" : "border-line bg-white text-slate-700 hover:border-blaze/50"
                }`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
      {tried && !valid && <p className="mt-4 text-sm font-semibold text-blaze" data-testid="wz-step7-error">Please choose a date and start time.</p>}
      <NavRow onBack={onBack} onNext={go} testid="wz-step7-next" />
    </div>
  );
};

export const Step8Extras = ({ data, patch, settings, onNext, onBack }) => {
  const services = settings?.extra_services || [];
  const boxPrice = settings?.box_price ?? 1;
  const stairsRate = settings?.stairs_rate ?? 20;
  const addonsTotal = data.boxes_qty * boxPrice + data.stairs_qty * stairsRate +
    services.filter((s) => data.extra_services.includes(s.id)).reduce((a, s) => a + s.price, 0);
  const toggle = (id) => {
    const cur = data.extra_services;
    patch({ extra_services: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const QtyCard = ({ label, unitPrice, qty, onQty, testid }) => (
    <div className="flex items-center justify-between gap-3 rounded-2xl border-2 border-line bg-white p-4" data-testid={testid}>
      <div>
        <p className="text-sm font-bold text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">${unitPrice} each{qty > 0 && <span className="font-semibold text-blaze"> · adding ${qty * unitPrice}</span>}</p>
      </div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onQty(Math.max(0, qty - 1))} disabled={qty === 0} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-slate-600 disabled:opacity-30" data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></button>
        <span className="w-6 text-center text-lg font-bold text-slate-900" data-testid={`${testid}-qty`}>{qty}</span>
        <button type="button" onClick={() => onQty(qty + 1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-slate-600" data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></button>
      </div>
    </div>
  );
  return (
    <div data-testid="wz-step8">
      <StepTitle title="Do you need any additional services?" sub="Optional — tap to add. Your quote total updates automatically." />
      <div className="space-y-3">
        <QtyCard label="Line Boxes" unitPrice={boxPrice} qty={data.boxes_qty} onQty={(v) => patch({ boxes_qty: v })} testid="wz-boxes" />
        <QtyCard label="Flight of Stairs" unitPrice={stairsRate} qty={data.stairs_qty} onQty={(v) => patch({ stairs_qty: v })} testid="wz-stairs" />
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {services.map((s) => (
          <SelectService key={s.id} label={s.label} price={s.price} active={data.extra_services.includes(s.id)} onClick={() => toggle(s.id)} testid={`wz-extra-${s.id}`} />
        ))}
        <SelectService label="No Extra Services" active={data.extra_services.length === 0 && data.boxes_qty === 0 && data.stairs_qty === 0} onClick={() => patch({ extra_services: [], boxes_qty: 0, stairs_qty: 0 })} testid="wz-extra-none" />
      </div>
      {addonsTotal > 0 && (
        <p className="mt-4 rounded-xl bg-blaze/[0.06] px-4 py-3 text-sm font-bold text-blaze" data-testid="wz-addons-total">
          Additional services adding ${addonsTotal} to your quote
        </p>
      )}
      <NavRow onBack={onBack} onNext={onNext} testid="wz-step8-next" />
    </div>
  );
};

const SelectService = ({ label, price, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center justify-between gap-3 rounded-2xl border-2 p-4 text-left transition-all duration-200 active:scale-[0.98] ${
      active ? "border-blaze bg-blaze/[0.06]" : "border-line bg-white hover:border-blaze/50"
    }`}
  >
    <span className={`text-sm font-bold ${active ? "text-blaze" : "text-slate-800"}`}>{label}</span>
    {price > 0 && <span className="text-xs font-semibold text-slate-500">+${price}</span>}
  </button>
);
